import type { DevToolsHealthStatus } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsPanel/index.types';
import type { WebRTCService } from 'Modules/softphone/services/WebRTCService';

/**
 * Worst-of across every open peer connection.
 *
 * Closed connections are skipped: after a merge or a transfer the old leg's
 * connection is expected to be closed, and counting it would report a failure
 * for a call that succeeded.
 */
const classify = (pc: RTCPeerConnection): DevToolsHealthStatus | null => {
  const connState = pc.connectionState;
  const iceState = pc.iceConnectionState;

  if (connState === 'closed') {
    return null;
  }

  if (
    connState === 'connected' &&
    (iceState === 'connected' || iceState === 'completed')
  ) {
    return 'healthy';
  }

  if (
    connState === 'failed' ||
    connState === 'disconnected' ||
    iceState === 'failed' ||
    iceState === 'disconnected'
  ) {
    return 'error';
  }

  // Connecting, checking, new — and anything else, which is still in flight.
  return 'warning';
};

export const getWebRTCHealthStatus = (
  activeCalls: { id: string }[],
  webrtcService: WebRTCService,
  conferenceRoomGuid: string | null
): DevToolsHealthStatus => {
  if (activeCalls.length === 0 && !conferenceRoomGuid) {
    return 'idle';
  }

  const ids = [
    ...activeCalls.map((call) => call.id),
    ...(conferenceRoomGuid ? [conferenceRoomGuid] : []),
  ];

  const statuses = ids
    .map((id) => webrtcService.getPeerConnection(id))
    .filter((pc): pc is RTCPeerConnection => Boolean(pc))
    .map(classify)
    .filter((status): status is DevToolsHealthStatus => status !== null);

  if (statuses.includes('error')) return 'error';
  if (statuses.includes('warning')) return 'warning';
  if (statuses.includes('healthy')) return 'healthy';

  return 'idle';
};
